import { useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { draftIdIn } from 'components/create-dialog/useCreateDraft';
import Loader from 'components/Loader';
import { useDefaultCurrencyCode } from 'hooks/useDefaultCurrencyCode';
import { useState } from 'react';
import { useGetProjectByIdQuery } from 'store/api/projects';
import { ProjectFormData } from 'types/projects';
import { useClassifiers } from 'utils/useClassifiers';
import { projectDtoToForm } from './projectFormMapping';
import ProjectPageForm from './ProjectPageForm';

/** The add form loaded with what a draft holds, read as the project's edit page reads it. */
const ProjectDraftAdd = ({ draftId }: { draftId: number }) => {
    const [initialData, setInitialData] = useState<ProjectFormData>();
    const { data: project } = useGetProjectByIdQuery(String(draftId), { refetchOnMountOrArgChange: true });
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: ['CURRENCY'] });

    // Frozen once loaded: a refetch must not reset what the user is typing.
    if (!initialData && project && !isLoadingClassifiers) {
        setInitialData(projectDtoToForm(project, classifiers));
    }

    return initialData ? <ProjectPageForm type="add" initialData={initialData} /> : <Loader />;
};

/** A blank project's form, mounted once the currencies are in: it starts in the firm's default currency. */
const ProjectBlankAdd = () => {
    const { isLoading: isLoadingClassifiers } = useClassifiers({ codes: ['CURRENCY'] });
    const { isLoading: isLoadingDefaultCurrency } = useDefaultCurrencyCode();
    return isLoadingClassifiers || isLoadingDefaultCurrency ? <Loader /> : <ProjectPageForm type="add" />;
};

/** The project create; its URL with `?draft=<id>` edits that draft instead of a blank project. */
const ProjectPageAdd = () => {
    const draftId = draftIdIn(useCreateRoute().location.search);
    return draftId === null ? <ProjectBlankAdd /> : <ProjectDraftAdd key={draftId} draftId={draftId} />;
};

export default ProjectPageAdd;
